import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/api";
import { fmtMoney, fmtNumber, StatusPill, AgingChip } from "../components/ops/opsUtils";
import { ArrowLeft, Mail, Phone, User, Scale, Receipt, ListChecks } from "lucide-react";
import { toast } from "sonner";
import GeneratePayoffModal from "../components/payoffs/GeneratePayoffModal";
import CreateTaskModal from "../components/tasks/CreateTaskModal";

export default function ClientProfile() {
  const { name } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayoff, setShowPayoff] = useState(false);
  const [showTask, setShowTask] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await api.get(`/operations/clients/${encodeURIComponent(name)}`);
      setProfile(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  })(); }, [name]);

  if (loading) return <div className="p-10 text-[#6B7280]" data-testid="client-profile-loading">Loading client…</div>;
  if (!profile) return <div className="p-10 text-[#6B7280]">Not found.</div>;
  const s = profile.summary;

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto" data-testid="client-profile">
      <Link to="/admin/clients" className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0A2540] mb-3" data-testid="back-to-clients">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All clients
      </Link>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">{profile.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#6B7280] mt-2">
              {s.primary_email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {s.primary_email}</span>}
              {s.primary_phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {s.primary_phone}</span>}
              {s.attorneys.length > 0 && (
                <span className="inline-flex items-center gap-1.5"><Scale className="w-3.5 h-3.5" />{" "}
                  {s.attorneys.slice(0, 2).map((e, i) => (
                    <Link key={e} to={`/admin/attorneys/${encodeURIComponent(e)}`} className="text-[#0066FF] hover:underline">{e}{i < Math.min(s.attorneys.length, 2) - 1 ? ", " : ""}</Link>
                  ))}
                  {s.attorneys.length > 2 && <span> +{s.attorneys.length - 2}</span>}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowTask(true)} data-testid="add-task-from-client"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#374151] text-sm font-medium transition-colors">
              <ListChecks className="w-3.5 h-3.5" /> Task
            </button>
            <button onClick={() => setShowPayoff(true)} data-testid="generate-payoff-from-client"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-medium transition-colors">
              <Receipt className="w-3.5 h-3.5" /> Generate payoff
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-6 pt-5 border-t border-[#F3F4F6]">
          <Stat label="Active exposure" value={fmtMoney(s.active_exposure)} accent />
          <Stat label="Total funded" value={fmtMoney(s.total_funded)} />
          <Stat label="Loans" value={`${fmtNumber(s.active_loan_count)} active · ${fmtNumber(s.total_loan_count)} total`} />
          <Stat label="Window" value={`${formatDate(s.first_funding)} → ${formatDate(s.last_funding)}`} small />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-baseline justify-between">
            <div className="font-medium text-[#0A2540]">Loans</div>
            <span className="text-xs text-[#6B7280]">{profile.loans.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="px-4 py-2.5 font-medium">Funded</th>
                <th className="px-4 py-2.5 font-medium text-right">Principal</th>
                <th className="px-4 py-2.5 font-medium text-right">Current payoff</th>
                <th className="px-4 py-2.5 font-medium">Aging</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {profile.loans.map((l, i) => (
                <tr key={l.loan_id} data-testid={`client-loan-row-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                    onClick={() => (window.location.href = `/admin/loans/${l.loan_id}`)}>
                  <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{formatDate(l.funding_date)}</td>
                  <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums font-medium">{fmtMoney(l.principal)}</td>
                  <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums">{fmtMoney(l.current_payoff)}</td>
                  <td className="px-4 py-2.5"><AgingChip bucket={l.aging_bucket} /></td>
                  <td className="px-4 py-2.5"><StatusPill status={l.current_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <div className="font-medium text-[#0A2540] mb-4">Activity timeline</div>
          {profile.events.length === 0 ? (
            <div className="text-sm text-[#6B7280] italic">No recorded events.</div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {profile.events.map((e, i) => (
                <div key={i} className="text-xs flex items-start gap-2 pb-2 border-b border-[#F3F4F6] last:border-b-0" data-testid={`client-event-${i}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#0066FF] mt-1.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[#0A2540] capitalize"><span className="font-medium">{e.event_type.replace("_", " ")}</span> {e.payload?.from && e.payload?.to && <span className="text-[#6B7280]">{e.payload.from} → {e.payload.to}</span>}</div>
                    <div className="text-[10px] text-[#9CA3AF] tabular-nums mt-0.5">{formatDate(e.created_at)} · {e.by_email || "system"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <GeneratePayoffModal open={showPayoff} onClose={() => setShowPayoff(false)} prefill={{ clientName: profile.name }} />
      <CreateTaskModal open={showTask} onClose={() => setShowTask(false)} prefill={{ related_client_name: profile.name }} />
    </div>
  );
}

function Stat({ label, value, accent, small }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      <div className={`tabular-nums ${accent ? "text-[#0066FF] font-display text-2xl font-semibold" : "text-[#0A2540] " + (small ? "text-sm" : "text-lg font-medium")}`}>{value}</div>
    </div>
  );
}
